import { inject, Injectable } from '@angular/core';
import { StorageService } from '../../services/storage';
import { CompiledMigration, MigrationDefinition, MigrationsDocument } from '../types/migration.types';
import { CURRENT_DATA_VERSION, BUILT_IN_MIGRATIONS } from '../tokens';

declare var PouchDB: any;

const CACHE_KEY = 'cached_migrations';

@Injectable({ providedIn: 'root' })
export class MigrationService {
  private readonly storageService = inject(StorageService);
  private readonly currentVersion = inject(CURRENT_DATA_VERSION);
  private readonly builtInMigrations = inject(BUILT_IN_MIGRATIONS);

  private migrations: Map<string, CompiledMigration> = new Map();
  private dbLatestVersion = -1;
  private initialized = false;

  // Wird später vom DataService gesetzt (vermeidet zirkuläre Abhängigkeit)
  private remoteDbGetter?: () => any | null;

  /**
   * Setzt die Funktion zum Abrufen der Remote-DB.
   * Wird vom DataService aufgerufen um zirkuläre Abhängigkeit zu vermeiden.
   */
  setRemoteDbGetter(getter: () => any | null): void {
    this.remoteDbGetter = getter;
  }

  /**
   * Initialisiert den Service - lädt Migrationen aus DB oder Cache
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      // 1. Versuche aus Remote-DB zu laden
      const doc = await this.loadFromDatabase();
      if (doc) {
        this.compileMigrations(doc);
        this.cacheLocally(doc);
      }
    } catch (error) {
      console.warn('Konnte Migrationen nicht aus DB laden, verwende Cache:', error);
      // 2. Fallback: Aus LocalStorage Cache laden
      const cached = this.loadFromCache();
      if (cached) {
        this.compileMigrations(cached);
      }
    }

    // 3. Finaler Fallback: App-interne Migrationen registrieren
    this.registerBuiltInMigrations();

    this.initialized = true;
  }

  /**
   * Lädt Migrationen aus CouchDB
   */
  private async loadFromDatabase(): Promise<MigrationsDocument | null> {
    const db = this.remoteDbGetter?.();
    if (!db) return null;

    try {
      return await db.get('_design/migrations') as MigrationsDocument;
    } catch (err: any) {
      if (err.status === 404) {
        return null;
      }
      throw err;
    }
  }

  /**
   * Kompiliert String-Funktionen zu ausführbaren Funktionen
   */
  private compileMigrations(doc: MigrationsDocument): void {
    this.dbLatestVersion = doc.latestVersion;

    for (const [key, migration] of Object.entries(doc.migrations)) {
      try {
        const compiled: CompiledMigration = {
          // eslint-disable-next-line @typescript-eslint/no-implied-eval
          upgrade: this.compileFunction(migration.upgrade),
          // eslint-disable-next-line @typescript-eslint/no-implied-eval
          downgrade: this.compileFunction(migration.downgrade),
          dataLossWarnings: migration.dataLossWarnings || []
        };
        this.migrations.set(key, compiled);
      } catch (error) {
        console.error(`Fehler beim Kompilieren der Migration ${key}:`, error);
      }
    }
  }

  /**
   * Kompiliert einen Funktions-String zu einer ausführbaren Funktion.
   * Unterstützt Arrow Functions, Function Expressions und Method Shorthand.
   */
  private compileFunction(fnString: string): (doc: any) => any {
    // Method shorthand (z.B. "upgrade(tt){...}") zu Function Expression konvertieren
    const methodShorthandRegex = /^(\w+)\s*\(/;
    const match = fnString.match(methodShorthandRegex);
    if (match && !fnString.startsWith('function')) {
      fnString = 'function ' + fnString;
    }
    // eslint-disable-next-line @typescript-eslint/no-implied-eval
    return new Function('return ' + fnString)() as (doc: any) => any;
  }

  /**
   * Speichert Migrationen für Offline-Nutzung (global storage)
   */
  private cacheLocally(doc: MigrationsDocument): void {
    try {
      this.storageService.setSync(CACHE_KEY, doc);
    } catch (error) {
      console.warn('Konnte Migrationen nicht cachen:', error);
    }
  }

  /**
   * Lädt Migrationen aus dem Cache (global storage)
   */
  private loadFromCache(): MigrationsDocument | null {
    try {
      const cached = this.storageService.getSync(CACHE_KEY);
      if (cached) {
        return cached as MigrationsDocument;
      }
    } catch (error) {
      console.warn('Fehler beim Laden des Migration-Cache:', error);
    }
    return null;
  }

  /**
   * Registriert App-interne Migrationen als Fallback.
   * Diese werden nur verwendet wenn DB und Cache nicht verfügbar sind.
   */
  private registerBuiltInMigrations(): void {
    for (const migration of this.builtInMigrations) {
      this.registerMigration(migration);
    }
  }

  /**
   * Registriert eine einzelne Migration (für App-interne Migrationen)
   */
  registerMigration(migration: MigrationDefinition): void {
    const key = `${migration.fromVersion}->${migration.toVersion}`;

    // Nur registrieren wenn nicht bereits aus DB geladen
    if (!this.migrations.has(key)) {
      this.migrations.set(key, {
        upgrade: migration.upgrade,
        downgrade: migration.downgrade,
        dataLossWarnings: migration.dataLossWarnings || []
      });
    }
  }

  /**
   * Migriert ein Dokument zur Zielversion
   */
  migrate(doc: any, targetVersion: number = this.currentVersion): any {
    // Sicherheitsnetz: Falls initialize() noch nicht aufgerufen wurde,
    // sofort Built-in Migrationen registrieren (synchron)
    if (!this.initialized) {
      console.warn('MigrationService.migrate() aufgerufen bevor initialize() abgeschlossen - registriere Built-in Migrationen');
      this.registerBuiltInMigrations();
      this.initialized = true;
    }

    let currentVersion = doc.version ?? -1;

    if (currentVersion === targetVersion) {
      return doc;
    }

    const direction = currentVersion < targetVersion ? 'upgrade' : 'downgrade';
    const step = direction === 'upgrade' ? 1 : -1;

    while (currentVersion !== targetVersion) {
      const nextVersion = currentVersion + step;
      const key = direction === 'upgrade'
        ? `${currentVersion}->${nextVersion}`
        : `${nextVersion}->${currentVersion}`;

      const migration = this.migrations.get(key);
      if (!migration) {
        throw new Error(`Keine Migration gefunden: ${key}`);
      }

      doc = direction === 'upgrade'
        ? migration.upgrade(doc)
        : migration.downgrade(doc);

      currentVersion = nextVersion;
    }

    return doc;
  }

  /**
   * Prüft ob neuere Migrationen in der DB existieren als die App kennt
   */
  hasNewerMigrations(): boolean {
    return this.dbLatestVersion > this.currentVersion;
  }

  /**
   * Gibt die höchste bekannte Version zurück
   */
  getLatestKnownVersion(): number {
    return Math.max(this.dbLatestVersion, this.currentVersion);
  }

  /**
   * Gibt Warnungen für eine Migration zurück
   */
  getDataLossWarnings(fromVersion: number, toVersion: number): string[] {
    const warnings: string[] = [];
    let current = fromVersion;
    const step = fromVersion < toVersion ? 1 : -1;

    while (current !== toVersion) {
      const next = current + step;
      const key = fromVersion < toVersion
        ? `${current}->${next}`
        : `${next}->${current}`;

      const migration = this.migrations.get(key);
      if (migration?.dataLossWarnings) {
        warnings.push(...migration.dataLossWarnings);
      }
      current = next;
    }

    return warnings;
  }

  /**
   * Prüft ob eine Migration für ein bestimmtes Versionspaar existiert
   */
  hasMigration(fromVersion: number, toVersion: number): boolean {
    const key = `${fromVersion}->${toVersion}`;
    return this.migrations.has(key);
  }

  /**
   * Gibt alle registrierten Migrations-Keys zurück (für Debugging)
   */
  getRegisteredMigrations(): string[] {
    return Array.from(this.migrations.keys());
  }
}
